import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { ErrorToast } from "../../../../Utils/SweetAlert";
import { useQuery } from "@tanstack/react-query";
import { GetPrices, GetSingleClientUser, GetSingleClientUserSubscriptions } from "../../../../Api/clientUser";
import NoImage from "../../../../assets/Images/no-image.png";
import BackButton from "../../../../Components/BackButton";
import Pagination from "../../../../Utils";
import { useEffect, useState } from "react";
import SubscriptionForm from "../SubscriptionForm";

const ClientUserDetail = () => {
  const location = useLocation();
  const id = location.state.id;
  const [loading, setLoading] = useState(true);
  const [subHistoryPage, setSubHistoryPage] = useState(0);
  const [subHistoryLimit, setSubHistoryLimit] = useState(5);
  const [subHistories, setSubHistories] = useState([]);
  const [subTotalCount, setSubTotalCount] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [monthlyPrice, setMonthlyPrice] = useState(null);
  const [yearlyPrice, setYearlyPrice] = useState(null);

  const { data, refetch } = useQuery({
    queryKey: ["get-single-client-user", id],
    queryFn: () => GetSingleClientUser({ id }),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server error.");
    },
  });

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await GetSingleClientUserSubscriptions({
        id,
        page: subHistoryPage === 0 ? 1 : subHistoryPage + 1,
        limit: subHistoryLimit,
      });
      const payload = response?.data || {};
      const list = payload?.subscriptions || [];
      const total = payload?.pagination?.totalCount || 0;
      setSubHistories(list);
      setSubTotalCount(total);

    } catch (error) {
      ErrorToast(error?.response?.data?.message || "Server Error.");
    } finally {
      setLoading(false);
    }
  }


  const clientUserData = data?.data || {};

  const handleSubHistoryPageChange = (e, newPage) => {
    setSubHistoryPage(newPage);
  };

  const handleSubHistoryLimitChange = (e) => {
    setSubHistoryPage(0);
    setSubHistoryLimit(parseInt(e.target.value, 10));
  };

  const paginatedSubHistories = subHistories;

  const handleGiveSubscription = (user) => {
    setSelectedUser(user);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
    setOpenModal(false);
  };

  const getPrice = async () => {
    try {
      const res = await GetPrices();
      if (res.status === 1) {
        setMonthlyPrice(res.data.monthly?.toString()?.includes(".00") ? res.data.monthly : `${res.data.monthly}.00`);
        setYearlyPrice(res.data.yearly.toString()?.includes(".00") ? res.data.yearly : `${res.data.yearly}.00`);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }

  useEffect(() => {
    if (id) {
      fetchSubscription();
    }
  }, [id, subHistoryPage, subHistoryLimit])

  useEffect(()=>{
    getPrice();
  },[])

  return (
    <>
      <Helmet>
        <title>OVE | User Detail</title>
      </Helmet>
      <div className="page-wrapper client-user-detail">
        <div className="content container-fluid">
          <div className="admin-wrapper">
            {/* Page Header */}
            <div className="page-header">
              <div className="col">
                <h1 className="page-title">User Detail</h1>
              </div>
              <div className="col-auto">
                <BackButton to="/admin/client-user-listing" />
              </div>
            </div>

            {/* Main Card */}
            <div className="col-sm-12">
              <div className="card-table">
                <div className="card-body">
                  {/* Profile Section */}
                  <div className="row mb-4 align-items-center">
                    <div className="col-md-3 text-center">
                      <img
                        src={clientUserData?.profilePicUrl || NoImage}
                        alt="Profile"
                        className="client-user-detail-profile-pic"
                      />

                    </div>
                    <div className="col-md-9">
                      <div className="row">
                        <div className="col-md-6">
                          <label className="d-block modal-label">Name</label>
                          <span className="d-block modal-value">
                            {clientUserData?.name || "-"}
                          </span>
                        </div>
                        <div className="col-md-6">
                          <label className="d-block modal-label">Email</label>
                          <span className="d-block modal-value">
                            {clientUserData?.email || "-"}
                          </span>
                        </div>
                      </div>
                      <div className="row mt-3">
                        <div className="col-md-6">
                          <label className="d-block modal-label">Date of Birth</label>
                          <span className="d-block modal-value">
                            {clientUserData?.dob
                              ? new Date(clientUserData?.dob).toLocaleDateString()
                              : "-"}
                          </span>
                        </div>
                        <div className="col-md-6">
                          {clientUserData?.subscriptionPlanType ? (
                            <>
                              <label className="d-block modal-label">Plan Type</label>
                              <span className="d-block modal-value">
                                {clientUserData?.subscriptionPlanType}
                              </span>
                            </>
                          ) : (
                            <button
                              onClick={() => handleGiveSubscription(clientUserData)}
                              className="primary-button action-btn"
                            >
                              Give Subscription
                            </button>
                          )}
                        </div>
                      </div>
                      {selectedUser && (
                        <SubscriptionForm user={selectedUser} onClose={handleCloseModal} openModal={openModal} refetch={refetch} yearlyPrice={yearlyPrice} monthlyPrice={monthlyPrice} fetchSubscription={fetchSubscription} />
                      )}

                      <div className="row mt-3">
                        <div className="col-md-4">
                          <label className="d-block modal-label">Age</label>
                          <span className="d-block modal-value">
                            {clientUserData?.age ? `${clientUserData.age} Years` : "-"}
                          </span>
                        </div>
                        <div className="col-md-4">
                          <label className="d-block modal-label">Puberty Count</label>
                          <span className="d-block modal-value">
                            {clientUserData?.pubertyCount || "-"}
                          </span>
                        </div>
                        <div className="col-md-4">
                          <label className="d-block modal-label">Start Period Age</label>
                          <span className="d-block modal-value">
                            {clientUserData?.startPeriodAge || "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Symptoms Section */}
                  <div className="row mt-4">
                    <div className="col-12">
                      <h4 className="sub-title mb-3 fs-4">Symptoms Experience</h4>
                      {clientUserData?.symptomsExperience?.length > 0 ? (
                        <div>
                          {clientUserData.symptomsExperience.map((s) => (
                            <span key={s._id} className="client-user-detail-symptom-badge">
                              {s.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted">No symptoms recorded.</p>
                      )}

                    </div>
                  </div>

                  {/* Period Histories Section */}
                  <div className="row mt-4">
                    <div className="col-12">
                      <h4 className="sub-title mb-3 fs-4">Last 6 Period Histories</h4>
                      {clientUserData?.periodHistories?.length > 0 ? (
                        <div className="col-sm-12 table-width">
                          <div className="cards-table card-table">
                            <div className="card-body p-0">
                              <div className="table-responsive custom-pagination">
                                <table className="table datatable table-bordered table-manager mb-0">
                                  <thead className="thead-light">
                                    <tr>
                                      <th className="col-name text-start" style={{ width: '15%' }}>#</th>
                                      <th className="col-email text-start" style={{ width: '42.5%' }}>Start Date</th>
                                      <th className="col-age text-start" style={{ width: '42.5%' }}>End Date</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {clientUserData.periodHistories.map((ph, index) => (
                                      <tr key={ph._id} className="table">
                                        <td>{index + 1}</td>
                                        <td className="pl-2 border-right">
                                          {ph.startDate
                                            ? new Date(ph.startDate).toLocaleDateString()
                                            : "-"}
                                        </td>
                                        <td className="pl-2">
                                          {ph.endDate ? (
                                            new Date(ph.endDate).toLocaleDateString()
                                          ) : (
                                            <span className="client-user-detail-ongoing-badge">Ongoing</span>
                                          )}
                                        </td>

                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted">No period history available.</p>
                      )}
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="col-12">
                      <h4 className="sub-title mb-3 fs-4">Subscription Histories</h4>
                      <div className="col-sm-12 table-width">
                        <div className="cards-table card-table">
                          <div className="card-body p-0">
                            <div className="table-responsive custom-pagination">
                              <table className="table datatable table-bordered table-manager mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th className="col-name text-start" style={{ width: '8%' }}>#</th>
                                    <th className="col-email text-start" style={{ width: '20%' }}>Plan Type</th>
                                    <th className="col-email text-start" style={{ width: '20%' }}>Start Date</th>
                                    <th className="col-age text-start" style={{ width: '20%' }}>End Date</th>
                                    <th className="col-age text-start" style={{ width: '20%' }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {loading && (
                                    <tr>
                                      <td align="center" colSpan={5}>
                                        <div className="d-flex justify-content-center align-items-center py-4">
                                          <div className="spinner-border loader-theme" role="status" />
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                  {!loading && paginatedSubHistories.map((sub, index) => (
                                    <tr key={sub._id} className="table">
                                      <td>{subHistoryPage * subHistoryLimit + index + 1}</td>
                                      <td className="pl-2 border-right text-capitalize">{sub?.planType || "-"}</td>
                                      <td className="pl-2 border-right">
                                        {sub?.startDate
                                          ? new Date(sub.startDate).toLocaleDateString()
                                          : "-"}
                                      </td>
                                      <td className="pl-2 border-right">
                                        {new Date(sub.endDate).toLocaleDateString()}
                                      </td>
                                      <td className="pl-2">
                                        {sub?.endDate && new Date(sub.endDate) < new Date()
                                          ? <span className="text-danger">Expired</span>
                                          : <span className="text-success">Active</span>}
                                      </td>
                                    </tr>
                                  ))}
                                  {!loading && paginatedSubHistories?.length === 0 && (
                                    <tr>
                                      <td className="text-center" colSpan={5}>
                                        No Records Found.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        <Pagination
                          totalItem={subTotalCount}
                          itemsPerPage={subHistoryLimit}
                          page={subTotalCount <= 0 ? 0 : subHistoryPage}
                          handleChangePage={handleSubHistoryPageChange}
                          handleChangeRowsPerPage={handleSubHistoryLimitChange}
                          colSpan={5}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientUserDetail;
